import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";
import { WebStorage } from "./async-store-web";

const storage = Platform.OS === "web" ? new WebStorage() : AsyncStorage;

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expirationMinutes: number;
}

export class AsyncStorageService {
  private static readonly STORE_KEYS = {
    cdn_cache: "cdn_cache",
    // Add more keys as needed
  } as const;

  /**
   * Generic method to set data in AsyncStorage with optional expiration
   */
  async setData<T>(
    key: string,
    data: T,
    expirationMinutes?: number
  ): Promise<void> {
    try {
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        expirationMinutes: expirationMinutes ?? 0,
      };

      await storage.setItem(key, JSON.stringify(cacheEntry));
      console.log(`[AsyncStorageService] Data cached for key: ${key}`);
    } catch (error) {
      console.error(
        `[AsyncStorageService] Failed to cache data for key ${key}:`,
        error
      );
      throw new Error(`Failed to cache data for key: ${key}`);
    }
  }

  /**
   * Generic method to get data from AsyncStorage with expiration check
   */
  async getData<T>(key: string): Promise<T | null> {
    try {
      const data = await storage.getItem(key);
      if (!data) return null;

      const cacheEntry: CacheEntry<T> = JSON.parse(data);

      // Check if data has expired
      if (cacheEntry.expirationMinutes > 0) {
        const expirationTime =
          cacheEntry.timestamp + cacheEntry.expirationMinutes * 60 * 1000;
        if (Date.now() > expirationTime) {
          await storage.removeItem(key);
          return null;
        }
      }

      return cacheEntry.data;
    } catch (error) {
      console.error(
        `[AsyncStorageService] Failed to get data for key ${key}:`,
        error
      );
      throw new Error(`Failed to get data for key: ${key}`);
    }
  }

  // Cache CDN data with optional expiration
  async setCdnCache(
    fileUrl: string,
    data: any,
    expirationMinutes: number = 129600 //3 months
  ): Promise<void> {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
        expirationMinutes,
      };
      await storage.setItem(
        `${AsyncStorageService.STORE_KEYS.cdn_cache}:${fileUrl}`,
        JSON.stringify(cacheEntry)
      );
      console.log(`[AsyncStorageService] CDN data cached for ${fileUrl}`);
    } catch (error) {
      console.error(
        `[AsyncStorageService] Failed to cache CDN data for ${fileUrl}:`,
        error
      );
      throw new Error("Failed to cache CDN data");
    }
  }

  // Retrieve cached CDN data
  async getCdnCache(fileUrl: string): Promise<any | null> {
    try {
      const cacheEntryString = await storage.getItem(
        `${AsyncStorageService.STORE_KEYS.cdn_cache}:${fileUrl}`
      );

      if (!cacheEntryString) {
        console.log(`[AsyncStorageService] No cache found for ${fileUrl}`);
        return null;
      }

      const cacheEntry = JSON.parse(cacheEntryString);
      const currentTime = Date.now();
      const cacheAge = (currentTime - cacheEntry.timestamp) / (1000 * 60);

      // Check if cache has expired
      if (cacheAge > cacheEntry.expirationMinutes) {
        console.log(`[AsyncStorageService] Cache expired for ${fileUrl}`);
        await this.removeCdnCache(fileUrl);
        return null;
      }

      console.log(`[AsyncStorageService] Cache retrieved for ${fileUrl}`);
      return cacheEntry.data;
    } catch (error) {
      console.error(
        `[AsyncStorageService] Failed to retrieve CDN cache for ${fileUrl}:`,
        error
      );
      return null;
    }
  }

  // Remove specific CDN cache entry
  async removeCdnCache(fileUrl: string): Promise<void> {
    try {
      await storage.removeItem(
        `${AsyncStorageService.STORE_KEYS.cdn_cache}:${fileUrl}`
      );
      console.log(`[AsyncStorageService] CDN cache removed for ${fileUrl}`);
    } catch (error) {
      console.error(
        `[AsyncStorageService] Failed to remove CDN cache for ${fileUrl}:`,
        error
      );
      throw new Error("Failed to remove CDN cache");
    }
  }

  // Clear all CDN cache entries
  async clearCdnCache(): Promise<void> {
    try {
      const keys = await storage.getAllKeys();
      const cdnCacheKeys = keys.filter((key) =>
        key.startsWith(`${AsyncStorageService.STORE_KEYS.cdn_cache}:`)
      );

      await storage.multiRemove(cdnCacheKeys);
      console.log("[AsyncStorageService] All CDN cache cleared");
    } catch (error) {
      console.error("[AsyncStorageService] Failed to clear CDN cache:", error);
      throw new Error("Failed to clear CDN cache");
    }
  }
}

export const asyncStorageService = new AsyncStorageService();
